<template>
  <div class="iq-card iq-card-sm">
    <div class="card-body">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h5 class="mb-0">{{ $t('pos.lbl_customer') }}</h5>
        <button type="button" class="btn btn-sm btn-outline-primary pos-tap" v-if="store.customer" @click="clearCustomer">
          {{ $t('pos.lbl_change_customer') }}
        </button>
      </div>

      <!-- Cliente seleccionado -->
      <template v-if="store.customer">
        <div class="d-flex flex-column gap-1">
          <strong>{{ store.customer.name }}</strong>
          <small class="text-muted" v-if="store.customer.identification">
            {{ identificationLabel(store.customer.identification_type) }}: {{ store.customer.identification }}
          </small>
          <small class="text-muted" v-else>{{ $t('pos.lbl_no_identification') }}</small>
          <small class="text-muted" v-if="store.customer.mobile">{{ store.customer.mobile }}</small>
        </div>

        <!--
          Aviso fiscal: sin identificacion el motor SRI resuelve CONSUMIDOR FINAL,
          y una factura a consumidor final no admite nota de credito nominativa.
        -->
        <AlertMessage class="mt-3 mb-0" variant="warning" v-if="!store.customer.identification" :message="$t('pos.lbl_final_consumer_warning')" />
      </template>

      <!-- Buscador -->
      <template v-else-if="!creating">
        <label class="form-label" for="pos-customer-search">{{ $t('pos.lbl_search_customer') }}</label>
        <div class="d-flex gap-2">
          <input
            id="pos-customer-search"
            type="search"
            class="form-control"
            :placeholder="$t('pos.lbl_search_customer_ph')"
            v-model="query"
            @keyup.enter="search"
          />
          <button type="button" class="btn btn-primary pos-tap" :disabled="searching || query.trim().length < 2" @click="search">
            <span v-if="searching" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            <span v-else>{{ $t('pos.lbl_search') }}</span>
          </button>
        </div>

        <AlertMessage class="mt-3 mb-0" :message="searchError" />

        <ul class="list-group mt-3" v-if="results.length">
          <li
            v-for="item in results"
            :key="`customer-${item.id}`"
            class="list-group-item list-group-item-action d-flex justify-content-between align-items-center pos-tap"
            role="button"
            tabindex="0"
            @click="select(item)"
            @keyup.enter="select(item)"
          >
            <span class="d-flex flex-column">
              <span>{{ item.name }}</span>
              <small class="text-muted">
                {{ item.identification || $t('pos.lbl_no_identification') }}
                <template v-if="item.mobile"> · {{ item.mobile }}</template>
              </small>
            </span>
            <i class="fa-solid fa-chevron-right" aria-hidden="true"></i>
          </li>
        </ul>

        <p class="text-muted mt-3 mb-0" v-else-if="searched">{{ $t('pos.lbl_no_customers') }}</p>

        <div class="d-flex gap-2 mt-3">
          <!-- Consumidor Final: rapido y disponible offline (viene en el catalogo). -->
          <button type="button" class="btn btn-outline-primary w-100 pos-tap" v-if="store.catalog.consumidor_final" @click="selectConsumidorFinal">
            <i class="fa-solid fa-user me-2" aria-hidden="true"></i>{{ $t('pos.lbl_consumidor_final') }}
          </button>
          <button type="button" class="btn btn-outline-secondary w-100 pos-tap" @click="startCreate">
            <i class="fa-solid fa-user-plus me-2" aria-hidden="true"></i>{{ $t('pos.lbl_new_customer') }}
          </button>
        </div>
      </template>

      <!-- Alta al vuelo -->
      <template v-else>
        <div class="row g-2">
          <!--
            Orden pensado para el flujo fiscal: PRIMERO el tipo + la identificacion,
            porque al completar la cedula/RUC se autollena la razon social (el
            nombre). Asi el cajero no teclea el nombre a mano si el gateway lo trae.
          -->
          <div class="col-md-6">
            <label class="form-label" for="pos-id-type">{{ $t('pos.lbl_identification_type') }} <span class="text-danger">*</span></label>
            <select id="pos-id-type" class="form-select" v-model="form.identification_type" @change="scheduleLookup">
              <option v-for="type in identificationTypes" :key="type.value" :value="type.value">{{ $t(type.label) }}</option>
            </select>
          </div>
          <div class="col-md-6">
            <label class="form-label" for="pos-id">{{ $t('pos.lbl_identification') }} <span class="text-danger">*</span></label>
            <input id="pos-id" type="text" class="form-control" v-model="form.identification" @input="scheduleLookup" />

            <!-- Estado del autollenado de razon social (previene el error 69 del SRI). -->
            <small class="d-flex align-items-center gap-1 mt-1 text-muted" v-if="lookingUp">
              <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>{{ $t('pos.lbl_looking_up_receptor') }}
            </small>
            <small class="d-flex align-items-center gap-1 mt-1" :class="lookupInvalid ? 'text-danger' : 'text-success'" v-else-if="lookupMessage">
              <i :class="lookupInvalid ? 'fa-solid fa-circle-exclamation' : 'fa-solid fa-id-card'" aria-hidden="true"></i>{{ lookupMessage }}
            </small>
          </div>
          <div class="col-md-6">
            <label class="form-label" for="pos-first-name">{{ $t('pos.lbl_first_name') }} <span class="text-danger">*</span></label>
            <input id="pos-first-name" type="text" class="form-control" v-model="form.first_name" />
          </div>
          <div class="col-md-6">
            <label class="form-label" for="pos-last-name">{{ $t('pos.lbl_last_name') }}</label>
            <input id="pos-last-name" type="text" class="form-control" v-model="form.last_name" />
          </div>
          <div class="col-md-6">
            <label class="form-label" for="pos-email">{{ $t('pos.lbl_email') }}</label>
            <input id="pos-email" type="email" class="form-control" v-model="form.email" />
          </div>
          <div class="col-md-6">
            <label class="form-label" for="pos-mobile">{{ $t('pos.lbl_mobile') }}</label>
            <input id="pos-mobile" type="text" class="form-control" v-model="form.mobile" />
          </div>
        </div>

        <AlertMessage class="mt-3 mb-0" :message="createError" :list="createErrorList" />

        <div class="d-flex gap-2 mt-3">
          <button type="button" class="btn btn-secondary pos-tap" @click="cancelCreate">{{ $t('pos.lbl_cancel') }}</button>
          <button type="button" class="btn btn-primary pos-tap" :disabled="saving" @click="create">
            <span v-if="saving" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            {{ $t('pos.lbl_create') }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import AlertMessage from './AlertMessage.vue'
import { usePos } from '../../store/pos'
import { posRequest, flattenErrors } from '../usePosRequest'
import { POS_CUSTOMER_SEARCH, POS_CUSTOMER_STORE, POS_CONSULTAR_IDENTIFICACION, IDENTIFICATION_TYPES } from '../constants/pos'

const store = usePos()
const { t } = useI18n()

const identificationTypes = IDENTIFICATION_TYPES

const query = ref('')
const results = ref([])
const searching = ref(false)
const searched = ref(false)
const searchError = ref('')

const creating = ref(false)
const saving = ref(false)
const createError = ref('')
const createErrorList = ref([])

// --- Autollenado de razon social (previene el error 69 del SRI) ---
let lookupTimer = null
const lookingUp = ref(false)
const lookupMessage = ref('')
// Ultimos valores puestos por la consulta: si el campo sigue igual, una consulta nueva
// puede reemplazarlo (lo tecleado a mano por el cajero no se toca).
const autollenado = { first_name: '', last_name: '' }
const lookupInvalid = ref(false)

const form = reactive({
  first_name: '',
  last_name: '',
  identification_type: '05',
  identification: '',
  email: '',
  mobile: ''
})

const identificationLabel = (type) => {
  const found = IDENTIFICATION_TYPES.find((item) => item.value === type)
  return found ? t(found.label) : t('pos.lbl_identification')
}

const emit = defineEmits(['auth-error'])

/** 401/403 no son errores de formulario: los sube la pantalla como estado global. */
const bubbleAuth = (res) => {
  if (res.httpStatus === 401 || res.httpStatus === 403) {
    emit('auth-error', res.httpStatus)
    return true
  }
  return false
}

const search = async () => {
  if (query.value.trim().length < 2) return

  searching.value = true
  searchError.value = ''

  const res = await posRequest(POS_CUSTOMER_SEARCH({ q: query.value.trim() }))

  searching.value = false
  searched.value = true

  if (!res.ok) {
    if (bubbleAuth(res)) return
    searchError.value = res.message || t('pos.lbl_unexpected_error')
    results.value = []
    return
  }

  results.value = Array.isArray(res.data) ? res.data : []
}

const select = (customer) => {
  store.setCustomer(customer)
  store.clearError()
  results.value = []
  searched.value = false
  query.value = ''
}

const clearCustomer = () => {
  store.setCustomer(null)
}

/** Selecciona el cliente CONSUMIDOR FINAL del catalogo (rapido y offline). */
const selectConsumidorFinal = () => {
  if (!store.catalog.consumidor_final) return
  store.setCustomer({ ...store.catalog.consumidor_final })
  store.clearError()
  results.value = []
  searched.value = false
  query.value = ''
}

const startCreate = () => {
  creating.value = true
  createError.value = ''
  createErrorList.value = []
}

const cancelCreate = () => {
  creating.value = false
  createError.value = ''
  createErrorList.value = []
  clearLookup()
}

const clearLookup = () => {
  if (lookupTimer) clearTimeout(lookupTimer)
  lookingUp.value = false
  lookupMessage.value = ''
  lookupInvalid.value = false
}

/** Longitud exacta que exige el backend por tipo (para no consultar a medias). */
const expectedLength = (type) => (type === '05' ? 10 : type === '04' ? 13 : null)

/** Programa la consulta ~500ms despues de la ultima tecla. */
const scheduleLookup = () => {
  if (lookupTimer) clearTimeout(lookupTimer)
  lookupTimer = setTimeout(lookupReceptor, 500)
}

/**
 * Consulta cedula/RUC y autollena la razon social (previene el error 69 del SRI).
 * Solo con la identificacion COMPLETA; el pasaporte no lo resuelve el gateway.
 * Fail-soft: cualquier fallo se traga en silencio (el cajero teclea el nombre).
 */
const lookupReceptor = async () => {
  const tipo = form.identification_type
  const identificacion = form.identification.trim().toUpperCase()
  const len = expectedLength(tipo)

  if (!len || identificacion.length !== len) {
    lookingUp.value = false
    lookupMessage.value = ''
    lookupInvalid.value = false
    return
  }

  lookingUp.value = true
  lookupMessage.value = ''
  lookupInvalid.value = false

  const res = await posRequest(POS_CONSULTAR_IDENTIFICACION(), { tipo, identificacion })

  // Se descarta si el cajero cambio el tipo o el numero mientras la llamada volaba.
  if (tipo !== form.identification_type || identificacion !== form.identification.trim().toUpperCase()) return

  lookingUp.value = false

  if (!res.ok) return // fail-soft: no bloquea el alta

  const data = res.data || {}
  lookupInvalid.value = data.valido === false
  lookupMessage.value = data.mensaje || ''

  const razon = (data.razon_social || '').trim()
  // Cedula: el backend separa apellidos y nombres (Registro Civil). RUC: razon social.
  const nombres = (data.datos?.nombres || '').trim()
  const apellidos = (data.datos?.apellidos || '').trim()
  const nuevoNombre = nombres || razon
  if (!nuevoNombre) return
  // Se reemplaza solo lo vacio o lo que puso una consulta anterior: nunca lo tecleado.
  const libre = (campo) => !form[campo].trim() || form[campo] === autollenado[campo]
  if (libre('first_name')) {
    form.first_name = nuevoNombre
    autollenado.first_name = nuevoNombre
  }
  if (libre('last_name')) {
    // Sociedad: sin apellidos (se vacia lo que dejo una consulta anterior).
    form.last_name = nombres ? apellidos : ''
    autollenado.last_name = form.last_name
  }
}

const create = async () => {
  saving.value = true
  createError.value = ''
  createErrorList.value = []

  const body = {
    first_name: form.first_name.trim(),
    last_name: form.last_name.trim() || null,
    identification_type: form.identification_type,
    // Se normaliza a mayusculas ANTES de enviar: la regla de pasaporte es
    // `^[A-Z0-9]{5,20}$` y se valida sobre el valor crudo (el `strtoupper` del
    // backend ocurre despues). Sin esto, un pasaporte en minusculas daria un 422
    // incomprensible para el cajero. En cedula/RUC (solo digitos) es inocuo.
    identification: form.identification.trim().toUpperCase(),
    email: form.email.trim() || null,
    mobile: form.mobile.trim() || null
  }

  const res = await posRequest(POS_CUSTOMER_STORE(), body)

  saving.value = false

  if (!res.ok) {
    if (bubbleAuth(res)) return
    // El mensaje del envelope se muestra tal cual (p.ej. cedula invalida,
    // identificacion duplicada): no se sustituye por un texto generico.
    createError.value = res.message || t('pos.lbl_unexpected_error')
    createErrorList.value = flattenErrors(res.errors)
    return
  }

  store.setCustomer(res.data)
  creating.value = false
  Object.assign(form, { first_name: '', last_name: '', identification_type: '05', identification: '', email: '', mobile: '' })
  autollenado.first_name = ''
  autollenado.last_name = ''
}
</script>
